import { generatePath } from 'react-router';

const MAX_DEPTH = 10;

/**
 * Walks `parentPath` recursively from a given route's template up to the
 * first root or until MAX_DEPTH is hit. Returns an array of entries
 * suitable for prepending onto an empty stack (B2 deep-link fallback).
 *
 * Pure relative to the supplied `findRoute(template)` lookup, which must
 * return the route object (or null) for a given template.
 */
export const buildParentChain = (pathTemplate, params, findRoute) => {
  const startRoute = findRoute(pathTemplate);
  if (!startRoute || !startRoute.additional?.breadcrumb) return [];

  // The current route may declare `parentParams` to seed values for
  // params that exist in a parent template but not in the current
  // URL. Example: an assessment-report route exposes `:status`, but
  // its parent (the patient dashboard tab) wants `:tableName`. The
  // assessment-report config sets `{ tableName: 'assessments' }` so
  // generatePath can resolve the parent path without throwing
  // `Missing ":tableName" param`. The seed is preserved across the
  // chain walk so deeper ancestors that also need the value still
  // see it; existing URL params always win on collision.
  let augmentedParams = {
    ...(startRoute.additional.breadcrumb.parentParams || {}),
    ...params,
  };

  const chain = [];
  let cursor = startRoute.additional.breadcrumb.parentPath;
  let depth = 0;

  while (cursor && depth < MAX_DEPTH) {
    const parent = findRoute(cursor);
    if (!parent || !parent.additional?.breadcrumb) break;

    chain.unshift({
      pathTemplate: parent.path,
      resolvedPath: generatePath(parent.path, augmentedParams),
      params: extractParamsForTemplate(parent.path, augmentedParams),
      isRoot: !!parent.additional.breadcrumb.isRoot,
    });

    if (parent.additional.breadcrumb.isRoot) break;
    // Any parentParams declared on this ancestor are folded in too,
    // so a multi-hop chain (e.g. report -> patient dashboard -> ...)
    // can layer defaults without each intermediate having to repeat
    // them.
    augmentedParams = {
      ...(parent.additional.breadcrumb.parentParams || {}),
      ...augmentedParams,
    };
    cursor = parent.additional.breadcrumb.parentPath;
    depth++;
  }

  if (depth >= MAX_DEPTH && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[breadcrumbs] buildParentChain exceeded MAX_DEPTH for "${pathTemplate}" — likely a cyclic parentPath.`
    );
  }

  return chain;
};

/**
 * Pick only the params referenced in the given template.
 * `generatePath` ignores extras, but storing only what the template uses
 * keeps stack entries compact and predictable.
 */
const extractParamsForTemplate = (template, allParams) => {
  const out = {};
  const keys = template.match(/:[A-Za-z_][\w]*/g) || [];
  keys.forEach(k => {
    const name = k.slice(1);
    if (allParams[name] !== undefined) out[name] = allParams[name];
  });
  return out;
};
